import { generateId } from "@fleet/utils";
import { createEffect, createMemo, createSignal, For, onCleanup, Show, untrack } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Play, RotateCcw, Square, Timer } from "@fleet/icons";
import { GlassSurface } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery, createPowerSyncSingle } from "@time-tracker/shared/solid";
import { showToast } from "@/components/ui/toast";
import { isTauriRuntime } from "@/lib/platform";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { ensureWorkspace } from "@/features/chronos-sync/workspace";
import { formatTimeParts } from "@/features/chronos-sync/helpers";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import {
  and,
  desc,
  eq,
  type PowerSyncDrizzleDB,
  project,
  type Project,
  task,
  type Task,
  time_entry,
  type TimeEntry,
} from "@time-tracker/powersync";
import {
  cn,
  liveProjectFilters,
  liveTaskFilters,
  liveTimeEntryFilters,
  patchRowVerified,
  queryKeys,
  updateUserPresenceMutation,
} from "@time-tracker/shared";

// Constants to avoid magic numbers

const colorMap = {
  purple: {
    text: "text-[#ebb2ff]",
    border: "border-[#ebb2ff]/20",
    glow: "shadow-[0_0_24px_rgba(235,178,255,0.15)]",
    badge: "bg-[#ebb2ff]/10 text-[#ebb2ff] border-[#ebb2ff]/20",
    bg: "bg-[#ebb2ff]",
  },
  amber: {
    text: "text-amber-300",
    border: "border-amber-500/20",
    glow: "shadow-[0_0_24px_rgba(245,158,11,0.15)]",
    badge: "bg-amber-500/10 text-amber-300 border-amber-500/20",
    bg: "bg-amber-500",
  },
  emerald: {
    text: "text-[#ceffdf]",
    border: "border-[#ceffdf]/20",
    glow: "shadow-[0_0_24px_rgba(206,255,223,0.15)]",
    badge: "bg-[#ceffdf]/10 text-[#ceffdf] border-[#ceffdf]/20",
    bg: "bg-[#ceffdf]",
  },
  blue: {
    text: "text-accent",
    border: "border-accent/20",
    glow: "shadow-[0_0_28px_var(--glow-0)]",
    badge: "bg-accent/12 text-accent border-accent/25",
    bg: "bg-accent",
  },
} as const;

const getProjectColorClass = (
  color: string | null | undefined,
  type: "text" | "border" | "bg" | "glow" | "badge",
) => {
  const c = color ?? "bg-blue-500";
  let theme: "purple" | "blue" | "amber" | "emerald" = "blue";

  if (c.includes("purple") || c.includes("CHRON")) {
    theme = "purple";
  } else if (c.includes("amber") || c.includes("orange") || c.includes("MKT")) {
    theme = "amber";
  } else if (c.includes("emerald") || c.includes("green") || c.includes("ADM")) {
    theme = "emerald";
  }

  const styles = colorMap[theme];
  return styles[type];
};

/**
 * Real presence: state is published to the team through user sync.
 *
 * `user` goes through `updateUserPresenceMutation` (→ `patchRowVerified`) like
 * any other preference: a direct `UPDATE` against the row can match
 * zero rows and return success if the PowerSync view is rebuilt at that
 * exact moment — see the docblock of `verified-write.ts`.
 */
async function setPresence(
  dbInstance: PowerSyncDrizzleDB,
  userId: string,
  status: "online" | "focus",
) {
  try {
    await updateUserPresenceMutation(dbInstance, userId, status);
  } catch (err) {
    console.error("Failed to update presence status", err);
  }
}

async function stopActiveTimer(
  params: {
    dbInstance: PowerSyncDrizzleDB;
    userId: string;
    activeId: string;
    projectName: string;
    description: string;
  },
  t: (m: MessageDescriptor) => string,
) {
  const { dbInstance, userId, activeId, projectName, description } = params;
  await patchRowVerified(dbInstance, time_entry, time_entry.id, activeId, {
    is_running: 0,
    end_time: new Date().toISOString(),
  });
  await setPresence(dbInstance, userId, "online");
  showToast(t(msg`Timer stopped`), "success");
  void triggerNotification(projectName, description, t);
}

async function startActiveTimer(
  params: {
    dbInstance: PowerSyncDrizzleDB;
    userId: string;
    projectId: string;
    descriptionStr: string;
    taskId?: string;
  },
  t: (m: MessageDescriptor) => string,
) {
  const { dbInstance, userId, projectId, descriptionStr, taskId } = params;
  await ensureWorkspace(dbInstance, userId);
  await dbInstance.insert(time_entry).values({
    id: generateId(),
    user_id: userId,
    project_id: projectId,
    task_id: taskId ?? null,
    description: descriptionStr,
    is_running: 1,
    start_time: new Date().toISOString(),
    source: "manual",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  await setPresence(dbInstance, userId, "focus");
  showToast(t(msg`Timer started`), "success");
}

async function handleQuickStartAction(
  params: {
    dbInstance: PowerSyncDrizzleDB;
    active: TimeEntry | null | undefined;
    userId: string;
    projectId: string;
    descriptionStr: string;
  },
  t: (m: MessageDescriptor) => string,
) {
  const { dbInstance, active, userId, projectId, descriptionStr } = params;
  if (active) {
    // Stop current running timer first (without notification noise)
    await patchRowVerified(dbInstance, time_entry, time_entry.id, active.id, {
      is_running: 0,
      end_time: new Date().toISOString(),
    });
  }

  await startActiveTimer({ dbInstance, userId, projectId, descriptionStr }, t);
}

async function handleStartPauseAction(
  params: {
    dbInstance: PowerSyncDrizzleDB;
    active: TimeEntry | null | undefined;
    projName: string;
    userId: string;
    selectedProjId: string | undefined;
    descriptionStr: string;
    taskId?: string;
  },
  t: (m: MessageDescriptor) => string,
) {
  const { dbInstance, active, projName, userId, selectedProjId, descriptionStr, taskId } = params;
  if (active) {
    await stopActiveTimer(
      {
        dbInstance,
        userId,
        activeId: active.id,
        projectName: projName,
        description: active.description ?? t(msg`No description`),
      },
      t,
    );
  } else {
    if (!selectedProjId) {
      showToast(t(msg`Please select a project`), "warning");
      return false;
    }
    await startActiveTimer(
      {
        dbInstance,
        userId,
        projectId: selectedProjId,
        descriptionStr,
        taskId,
      },
      t,
    );
  }
  return true;
}

function fetchProjects(drizzleInstance: PowerSyncDrizzleDB | null, workspaceId: string) {
  if (!drizzleInstance || !workspaceId) {
    return null;
  }
  return drizzleInstance
    .select()
    .from(project)
    .where(and(eq(project.workspace_id, workspaceId), ...liveProjectFilters()));
}

function fetchActiveTimer(
  drizzleInstance: PowerSyncDrizzleDB | null,
  currentUserId: string | null,
) {
  if (!drizzleInstance || !currentUserId) {
    return null;
  }
  return drizzleInstance
    .select()
    .from(time_entry)
    .where(
      and(
        eq(time_entry.user_id, currentUserId),
        eq(time_entry.is_running, 1),
        ...liveTimeEntryFilters(),
      ),
    )
    .limit(1);
}

async function triggerNotification(
  projectName: string,
  descriptionStr: string,
  t: (m: MessageDescriptor) => string,
) {
  if (isTauriRuntime()) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("trigger_sync_notification", {
        title: t(msg`Timer Stopped`),
        body: t(msg`Logged time for ${projectName} - ${descriptionStr}`),
      });
    } catch (err) {
      console.error("Failed to trigger native notification:", err);
    }
  }
}

export function PulseTimer() {
  const { userId } = useSession();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();

  // Deep-link for the timer preselection: `?project=<id>&task=<id>&desc=...`
  // lets you prepare and share a timer (project/task/description) that
  // reopens identically in another browser. The ACTIVE timer is already addressable via the DB; this
  // covers only the preselection before starting. `strict:false` + `to:"."` because
  // PulseTimer lives in `/chronos` and we preserve the rest of the params (e.g. `?ws=`).
  const timerSearch = useSearch({ strict: false });
  const navigate = useNavigate();

  const urlStr = (key: "project" | "task" | "desc"): string => {
    const v = (timerSearch() as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  };

  const [description, setDescription] = createSignal(urlStr("desc"));
  const [selectedProjectId, setSelectedProjectId] = createSignal(urlStr("project"));
  const [selectedTaskId, setSelectedTaskId] = createSignal(urlStr("task"));
  const [currentTime, setCurrentTime] = createSignal(Date.now());

  // Write the preselection to the URL (only with no active timer; the active one wins).
  // We omit empty values for clean links and preserve other params.
  const syncTimerUrl = () => {
    if (activeTimer.data) {
      return;
    }
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        project: selectedProjectId() || undefined,
        task: selectedTaskId() || undefined,
        desc: description().trim() || undefined,
      }),
      replace: true,
    });
  };

  // Clear the preselection from the URL (when starting the timer it no longer
  // applies: the active state lives in the DB).
  const clearTimerUrl = () => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        project: undefined,
        task: undefined,
        desc: undefined,
      }),
      replace: true,
    });
  };

  // Subscribe to all projects in Drizzle
  const projects = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.projects(activeWorkspaceId()),
    db: () => db(),
    queryFn: () => fetchProjects(drizzle(), activeWorkspaceId()),
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  // Pending tasks for the selected project (optional association)
  const projectTasks = createPowerSyncQuery<Task>({
    queryKey: () => queryKeys.chronos.timerTasks(selectedProjectId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const projId = selectedProjectId();
      if (!dbInstance || !projId) {
        return null;
      }
      return dbInstance
        .select()
        .from(task)
        .where(and(eq(task.project_id, projId), eq(task.completed, 0), ...liveTaskFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(selectedProjectId()),
    suspense: false,
  });

  // If the project changes, the selected task no longer applies
  createEffect(() => {
    const tasks = projectTasks.data ?? [];
    if (selectedTaskId() && !tasks.some((row) => row.id === selectedTaskId())) {
      setSelectedTaskId("");
    }
  });

  // Last completed entry (for "Resume last")
  const lastEntry = createPowerSyncSingle<TimeEntry>({
    queryKey: () => queryKeys.chronos.lastEntry(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(
          and(
            eq(time_entry.user_id, userId()),
            eq(time_entry.is_running, 0),
            ...liveTimeEntryFilters(),
          ),
        )
        .orderBy(desc(time_entry.start_time))
        .limit(1);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  const handleResumeLast = async () => {
    const dbInstance = drizzle();
    const last = lastEntry.data;
    if (!dbInstance || !last?.project_id) {
      return;
    }
    try {
      await startActiveTimer(
        {
          dbInstance,
          userId: userId(),
          projectId: last.project_id,
          descriptionStr: last.description ?? "",
        },
        t,
      );
    } catch (err) {
      console.error("Error resuming last entry:", err);
      showToast(t(msg`Action failed`), "error");
    }
  };

  // Subscribe to the active timer query (is_running === 1)
  const activeTimer = createPowerSyncSingle<TimeEntry>({
    queryKey: () => queryKeys.chronos.activeTimer(userId()),
    db: () => db(),
    queryFn: () => fetchActiveTimer(drizzle(), userId()),
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Find active project to display when running
  const activeProject = () => {
    const activeId = activeTimer.data?.project_id;
    return activeId && projects.data
      ? (projects.data.find((p) => p.id === activeId) ?? null)
      : null;
  };

  // Auto-initialize selectedProjectId when projects are loaded
  createEffect(() => {
    const list = projects.data;
    const firstId = list?.[0]?.id;
    if (firstId && !selectedProjectId()) {
      setSelectedProjectId(firstId);
    }
  });

  // URL → signals (back/forward, pasted link). Applies only with no active timer, to
  // avoid overwriting the current entry. Idempotent on reload.
  createEffect(() => {
    if (activeTimer.data) {
      return;
    }
    // Comparisons in `untrack`: only react to EXTERNAL URL changes; if
    // it tracked the signals it writes, editing project/task/description
    // state-first re-fires them with the OLD URL and reverts them (bug #15b / CC-16).
    const p = urlStr("project");
    const tk = urlStr("task");
    const d = urlStr("desc");
    if (p && untrack(() => p !== selectedProjectId())) {
      setSelectedProjectId(p);
    }
    if (untrack(() => tk !== selectedTaskId())) {
      setSelectedTaskId(tk);
    }
    if (untrack(() => d !== description())) {
      setDescription(d);
    }
  });

  // Keep a ticking clock when active timer is running using requestAnimationFrame (60fps)
  createEffect(() => {
    if (activeTimer.data) {
      let animId = 0;
      const updateTime = () => {
        setCurrentTime(Date.now());
        animId = requestAnimationFrame(updateTime);
      };
      animId = requestAnimationFrame(updateTime);
      onCleanup(() => {
        cancelAnimationFrame(animId);
      });
    }
  });

  // Calculate elapsed milliseconds reactively
  const elapsedMs = () => {
    const startStr = activeTimer.data?.start_time;
    if (!startStr) {
      return 0;
    }
    const start = new Date(startStr).getTime();
    return Math.max(currentTime() - start, 0);
  };

  // Memoized time parts
  const timeParts = createMemo(() => formatTimeParts(elapsedMs()));

  const handleQuickStart = async (projectId: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    try {
      await handleQuickStartAction(
        {
          dbInstance,
          active: activeTimer.data,
          userId: userId(),
          projectId,
          descriptionStr: description() || t(msg`Quick started session`),
        },
        t,
      );
      setDescription("");
      clearTimerUrl();
    } catch (err) {
      console.error("Error starting quick project:", err);
      showToast(t(msg`Action failed`), "error");
    }
  };

  const handleStartPause = async () => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      showToast(t(msg`Database not initialized`), "error");
      return;
    }

    try {
      const firstProj = projects.data?.[0];
      const selectedProjId = selectedProjectId() === "" ? firstProj?.id : selectedProjectId();
      const projName = activeProject()?.name ?? t(msg`Activity`);

      const success = await handleStartPauseAction(
        {
          dbInstance,
          active: activeTimer.data,
          projName,
          userId: userId(),
          selectedProjId,
          descriptionStr: description(),
          taskId: selectedTaskId() || undefined,
        },
        t,
      );

      if (success) {
        setDescription("");
        setSelectedTaskId("");
        clearTimerUrl();
      }
    } catch (err) {
      console.error("Error toggling timer:", err);
      showToast(t(msg`Action failed`), "error");
    }
  };

  const containerClass = createMemo(() => {
    const activeColor = activeProject()?.color;
    return activeTimer.data
      ? getProjectColorClass(activeColor, "glow")
      : "shadow-[0_0_24px_rgba(255,255,255,0.02)]";
  });

  const ambientGlowClass = createMemo(() => {
    const activeColor = activeProject()?.color;
    return getProjectColorClass(activeColor, "bg");
  });

  const headerBadgeClass = createMemo(() => {
    const activeColor = activeProject()?.color;
    return activeTimer.data
      ? getProjectColorClass(activeColor, "badge")
      : "bg-white/[0.03] border-white/10 text-white/60";
  });

  const ringClass = createMemo(() =>
    activeTimer.data
      ? "animate-breathe border-current opacity-20 scale-105 blur-[3px]"
      : "opacity-0 scale-95",
  );

  const ringColor = createMemo(() => {
    const activeColor = activeProject()?.color;
    return activeColor ? getProjectColorClass(activeColor, "text") : "var(--accent)";
  });

  const displayColorClass = createMemo(() => (activeTimer.data ? "text-white" : "text-white/40"));

  const activeProjectBadgeClass = createMemo(() => {
    const activeColor = activeProject()?.color;
    return getProjectColorClass(activeColor, "badge");
  });

  const selectedProjVal = createMemo(() => activeTimer.data?.project_id ?? selectedProjectId());

  const descriptionVal = createMemo(() => activeTimer.data?.description ?? description());

  const actionButtonClass = createMemo(() =>
    activeTimer.data
      ? "bg-danger text-danger-foreground border-danger/30 hover:bg-danger/90"
      : "bg-accent text-accent-foreground border-accent/30 hover:bg-accent/90",
  );

  // The local DB must be ready before allowing start/stop/resume:
  // without `drizzle()` the handlers cannot write and the action would be a no-op.
  const isReady = () => Boolean(drizzle());

  return (
    <GlassSurface
      class={cn(
        "transition-[border-color,box-shadow,background-color] duration-700 overflow-hidden bg-[#121212] border border-white/10 rounded-[32px] p-6 relative flex flex-col justify-between min-h-[500px]",
        containerClass(),
      )}
    >
      {/* Decorative ambient breathing background glow when running */}
      <Show when={activeTimer.data}>
        <div
          class={cn(
            "absolute -right-20 -top-20 w-44 h-44 rounded-full blur-[60px] animate-pulse pointer-events-none opacity-40",
            ambientGlowClass(),
          )}
        />
      </Show>

      {/* Header */}
      <div class="flex items-center justify-between mb-4 z-10">
        <div class="flex items-center gap-3">
          <div
            class={cn(
              "p-2.5 rounded-2xl border transition-[color,background-color,border-color] duration-500",
              headerBadgeClass(),
            )}
          >
            <Timer class="h-4.5 w-4.5" />
          </div>
          <div>
            <h2 class="text-sm font-bold tracking-wider text-white uppercase font-mono">
              {t(msg`Pulse Tracker`)}
            </h2>
            <p class="text-[10px] text-white/45 uppercase tracking-widest font-mono">
              {t(msg`Real-time OLED Sync`)}
            </p>
          </div>
        </div>
        <Show when={activeTimer.data}>
          <span class="flex h-2 w-2 relative">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span class="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        </Show>
      </div>

      {/* Large Centered Display */}
      <div class="flex flex-col items-center justify-center py-6 relative z-10 select-none">
        {/* Breathing perimeter ring */}
        <div
          class={cn(
            "absolute w-64 h-64 rounded-full border border-transparent transition-[color,border-color,box-shadow] duration-1000 pointer-events-none z-0",
            ringClass(),
          )}
          style={{
            color: ringColor(),
          }}
        />

        <div
          class={cn(
            "text-6xl md:text-7.5xl font-mono font-bold tracking-tighter tabular-nums flex items-baseline gap-0.5 z-10 transition-colors duration-500",
            displayColorClass(),
          )}
        >
          <span>{timeParts().mainPart}</span>
          <span class="text-3xl text-white/50 font-medium font-mono">.{timeParts().msPart}</span>
        </div>

        <Show
          when={activeTimer.data}
          fallback={
            <p class="text-[10px] text-white/30 font-mono mt-4 uppercase tracking-widest">
              {t(msg`System Idle`)}
            </p>
          }
        >
          <div class="mt-4 flex flex-col items-center gap-1.5 z-10 text-center px-4">
            <span
              class={cn(
                "text-[9px] font-bold uppercase tracking-widest font-mono border px-2 py-0.5 rounded-full",
                activeProjectBadgeClass(),
              )}
            >
              {activeProject()?.code ?? t(msg`TRACKING`)}
            </span>
            <p class="text-xs text-white/70 font-mono truncate max-w-xs">
              {activeTimer.data?.description ?? t(msg`Active Tracking`)}
            </p>
          </div>
        </Show>
      </div>

      {/* Form Fields: Subtle OLED trough inputs with bottom labels */}
      <div class="space-y-4 my-4 z-10">
        <div class="flex flex-col-reverse gap-1">
          <Select
            id="project-select"
            aria-label={t(msg`Project`)}
            value={selectedProjVal()}
            onChange={(v) => {
              setSelectedProjectId(v);
              syncTimerUrl();
            }}
            disabled={Boolean(activeTimer.data)}
            class="text-xs font-mono"
            options={(projects.data ?? []).map((p) => ({
              value: p.id,
              label: `${p.name} (${p.code})`,
            }))}
            emptyAction={{
              label: t(msg`Create a project`),
              href: "/chronos/projects",
              description: t(msg`No projects yet`),
            }}
          />
          <label
            for="project-select"
            class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono transition-colors duration-300"
          >
            {t(msg`Project`)}
          </label>
        </div>

        <Show when={(projectTasks.data ?? []).length > 0}>
          <div class="flex flex-col-reverse gap-1">
            <Select
              id="task-select"
              aria-label={t(msg`Task (optional)`)}
              value={selectedTaskId()}
              onChange={(v) => {
                setSelectedTaskId(v);
                syncTimerUrl();
              }}
              disabled={Boolean(activeTimer.data)}
              class="text-xs font-mono"
              options={[
                { value: "", label: `— ${t(msg`No task`)} —` },
                ...(projectTasks.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.name,
                })),
              ]}
            />
            <label
              for="task-select"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono transition-colors duration-300"
            >
              {t(msg`Task (optional)`)}
            </label>
          </div>
        </Show>

        <div class="flex flex-col-reverse gap-1">
          <input
            id="description-input"
            type="text"
            placeholder={t(msg`e.g. Design Dashboard Prototypes`)}
            value={descriptionVal()}
            onInput={(e) => {
              setDescription(e.currentTarget.value);
              syncTimerUrl();
            }}
            disabled={Boolean(activeTimer.data)}
            class="peer w-full bg-black/40 border border-white/10 focus:border-accent/50 focus:ring-1 focus:ring-accent/25 rounded-[16px] px-4 py-3 text-xs focus:outline-none text-white placeholder-white/20 transition-[border-color,box-shadow,opacity] duration-300 font-mono disabled:opacity-50"
          />
          <label
            for="description-input"
            class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono peer-focus:text-accent transition-colors duration-300"
          >
            {t(msg`What are you working on?`)}
          </label>
        </div>
      </div>

      {/* Quick Start Bento Grid - Optimized for Mobile Thumb Zone */}
      <div class="space-y-2.5 border-t border-white/5 pt-4 z-10">
        <span class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono block">
          {t(msg`Quick Start Projects`)}
        </span>
        <div class="grid grid-cols-2 gap-2">
          <For each={projects.data?.slice(0, 4)}>
            {(p) => {
              const isSelected = () => activeTimer.data?.project_id === p.id;
              const hasDotColor = () => p.color ?? "bg-blue-500";
              return (
                <button
                  type="button"
                  onClick={() => void handleQuickStart(p.id)}
                  disabled={!isReady()}
                  class={cn(
                    "flex flex-col items-start p-3.5 rounded-[20px] border text-left transition-[transform,background-color,border-color] duration-300 will-change-transform relative group overflow-hidden cursor-pointer active:scale-98 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:active:scale-100",
                    isSelected()
                      ? getProjectColorClass(p.color, "badge")
                      : "border-white/5 bg-white/[0.01] hover:bg-white/[0.03] hover:border-white/10 text-white",
                  )}
                >
                  <span
                    class={cn(
                      "h-1.5 w-1.5 rounded-full mb-1.5 shadow-sm",
                      hasDotColor().startsWith("bg-") ? hasDotColor() : "bg-blue-500",
                    )}
                  />
                  <span class="text-[11px] font-bold font-mono tracking-wide line-clamp-1">
                    {p.name}
                  </span>
                  <span class="text-[9px] text-white/40 font-mono mt-0.5 uppercase tracking-widest">
                    {p.code}
                  </span>
                  <div class="absolute right-3.5 bottom-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Play class="h-3 w-3 text-white/60" />
                  </div>
                </button>
              );
            }}
          </For>
        </div>
      </div>

      {/* Action Controls - Start/Stop único; "Reanudar última" cuando está parado */}
      <div class="flex items-center gap-3 pt-4 z-10">
        <button
          type="button"
          onClick={handleStartPause}
          disabled={!isReady()}
          class={cn(
            "flex-1 font-bold py-3.5 rounded-full active:scale-[0.98] transition-[transform,background-color,color,border-color] duration-200 will-change-transform flex items-center justify-center gap-2 cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] border text-xs uppercase tracking-widest font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100",
            actionButtonClass(),
          )}
        >
          <Show
            when={activeTimer.data}
            fallback={
              <>
                <Play class="h-3.5 w-3.5" /> {t(msg`Start`)}
              </>
            }
          >
            <Square class="h-3.5 w-3.5 fill-current" /> {t(msg`Stop`)}
          </Show>
        </button>

        <Show when={!activeTimer.data && lastEntry.data?.project_id}>
          <button
            type="button"
            onClick={() => void handleResumeLast()}
            disabled={!isReady()}
            title={t(msg`Resume: {what}`, {
              what: lastEntry.data?.description ?? t(msg`last activity`),
            })}
            class="px-5 py-3.5 bg-white/[0.03] text-white/70 border border-white/10 hover:bg-white/[0.08] active:scale-[0.98] transition-[transform,background-color,color] duration-200 will-change-transform flex items-center justify-center gap-2 rounded-full cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] font-mono text-[10px] uppercase tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            <RotateCcw class="h-3.5 w-3.5" /> {t(msg`Resume`)}
          </button>
        </Show>
      </div>
    </GlassSurface>
  );
}
